import { useEffect, useMemo, useRef, useState } from 'react';
import { chargerCatalogue, chercher, dureeLisible, episodesDe } from '../lib/catalogue';
import { affiche } from '../lib/images';
import Participants from '../ui/Participants';
import { useParticipants } from '../discord/useParticipants';
import Chargement from './Chargement';
import Erreur from './Erreur';

/**
 * La bibliothèque, pensée pour un panneau étroit.
 *
 * Deux colonnes d'affiches plutôt qu'une grille de site : dans une Activity on
 * a la largeur d'un téléphone, même sur un grand écran. La recherche est en
 * haut parce que c'est le premier geste quand on sait déjà quel titre lancer.
 *
 * Le catalogue compte ~900 titres. Tout poser d'un coup ferait 900 cartes dans
 * le DOM d'une iframe : on en rend une tranche, et la suivante arrive quand on
 * approche du bas. Sans bouton — il n'y a rien à décider, seulement à défiler.
 */

const PAR_TRANCHE = 60;

export default function Catalogue({ onChoisir }) {
  const participants = useParticipants();
  const [titres, setTitres] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [requete, setRequete] = useState('');
  const [serieOuverte, setSerieOuverte] = useState(null);
  const [limite, setLimite] = useState(PAR_TRANCHE);
  const sentinelleRef = useRef(null);

  useEffect(() => {
    let vivant = true;

    chargerCatalogue()
      .then((liste) => { if (vivant) setTitres(liste); })
      .catch((e) => { if (vivant) setErreur(e.message); });

    return () => { vivant = false; };
  }, []);

  const visibles = useMemo(
    () => (titres ? chercher(titres, requete) : []),
    [titres, requete],
  );

  // Une nouvelle recherche repart du haut : garder l'ancienne limite ferait
  // afficher des centaines de résultats d'un coup.
  useEffect(() => { setLimite(PAR_TRANCHE); }, [requete]);

  useEffect(() => {
    const cible = sentinelleRef.current;
    if (!cible) return undefined;

    const observateur = new IntersectionObserver(
      ([entree]) => {
        if (entree.isIntersecting) setLimite((actuelle) => actuelle + PAR_TRANCHE);
      },
      // On charge la tranche suivante avant d'atteindre le bas, pour que le
      // défilement ne marque jamais d'arrêt.
      { rootMargin: '400px' },
    );

    observateur.observe(cible);
    return () => observateur.disconnect();
  }, [visibles.length, limite]);

  if (erreur) return <Erreur message={erreur} onReessayer={() => window.location.reload()} />;
  if (!titres) return <Chargement texte="Chargement de la bibliothèque…" />;

  if (serieOuverte) {
    return (
      <ListeEpisodes
        serie={serieOuverte}
        onRetour={() => setSerieOuverte(null)}
        onChoisir={onChoisir}
      />
    );
  }

  const rendus = visibles.slice(0, limite);

  return (
    <div className="page">
      {/* Qui attend le film : la séance existe avant qu'on ait choisi. */}
      <Participants participants={participants} />

      <input
        className="recherche"
        type="search"
        placeholder="Chercher un titre…"
        value={requete}
        onChange={(e) => setRequete(e.target.value)}
      />

      <p className="compteur">
        {visibles.length} titre{visibles.length > 1 ? 's' : ''}
        {requete.trim() ? ` pour « ${requete.trim()} »` : ' dans la bibliothèque'}
      </p>

      {visibles.length === 0 ? (
        <p className="discret centre-texte">Rien ne correspond.</p>
      ) : (
        <>
          <div className="grille">
            {rendus.map((titre) => (
              <Affiche
                key={titre.id}
                titre={titre}
                onClic={() => (titre.isGroupedSeries ? setSerieOuverte(titre) : onChoisir(titre))}
              />
            ))}
          </div>

          {rendus.length < visibles.length && (
            <div ref={sentinelleRef} className="sentinelle">
              <div className="rond" />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function Affiche({ titre, onClic }) {
  const image = affiche(titre.poster);
  const duree = dureeLisible(titre);

  return (
    <button type="button" className="carte" onClick={onClic}>
      {image
        ? <img src={image} alt="" loading="lazy" />
        : <div className="sans-affiche">{titre.isGroupedSeries ? '📺' : '🎬'}</div>}

      <span className="carte-titre">{titre.title}</span>
      <span className="carte-detail">
        {titre.isGroupedSeries
          ? `${titre.episodeCount} épisode${titre.episodeCount > 1 ? 's' : ''}`
          : [titre.year, duree].filter(Boolean).join(' · ')}
      </span>
    </button>
  );
}

function ListeEpisodes({ serie, onRetour, onChoisir }) {
  const episodes = episodesDe(serie);

  return (
    <div className="page">
      <button type="button" className="retour" onClick={onRetour}>← Retour</button>
      <h2 className="titre-section">{serie.title}</h2>
      <p className="compteur">{episodes.length} épisode{episodes.length > 1 ? 's' : ''}</p>

      <div className="liste">
        {episodes.map((episode) => (
          <button
            type="button"
            key={episode.id}
            className="ligne"
            onClick={() => onChoisir(episode)}
          >
            <b>S{episode.season}E{episode.episode}</b>
            <span>{episode.episodeTitle || episode.episode_title || 'Épisode'}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
